import { useState } from 'react';
import { MessageCircle, Check } from 'lucide-react';

export const CommentMarker = ({
  comment,
  zoom,
  pan,
  isSelected,
  onClick,
  currentUserId,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate position
  const x = (comment.x || 0) * zoom + pan.x;
  const y = (comment.y || 0) * zoom + pan.y;

  const replyCount = comment.replies?.length || 0;
  const isResolved = comment.resolved;
  const isOwn = comment.user_id === currentUserId;

  return (
    <div
      className={`absolute cursor-pointer transition-transform ${
        isSelected ? 'z-50 scale-110' : 'z-40'
      }`}
      style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -100%)',
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(comment);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`comment-marker-${comment.id}`}
    >
      {/* Comment Pin */}
      <div className="relative">
        {/* Pin icon */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-all ${
            isResolved
              ? 'bg-green-500'
              : isSelected
              ? 'bg-blue-600'
              : isOwn
              ? 'bg-blue-500'
              : 'bg-purple-500'
          } ${isHovered && !isSelected ? 'ring-2 ring-white ring-offset-2' : ''}`}
        >
          {isResolved ? (
            <Check size={16} className="text-white" />
          ) : (
            <MessageCircle size={16} className="text-white" />
          )}
        </div>

        {/* Reply count badge */}
        {replyCount > 0 && !isResolved && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
            {replyCount > 9 ? '9+' : replyCount}
          </div>
        )}

        {/* Pin tail */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent ${
            isResolved
              ? 'border-t-green-500'
              : isSelected
              ? 'border-t-blue-600'
              : isOwn
              ? 'border-t-blue-500'
              : 'border-t-purple-500'
          }`}
          style={{ top: '100%', marginTop: '-1px' }}
        />
      </div>

      {/* Hover preview */}
      {isHovered && !isSelected && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-64 pointer-events-none">
          <div className="flex items-start gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                isOwn ? 'bg-blue-500' : 'bg-purple-500'
              }`}
            >
              {comment.user_name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {comment.user_name}
                </span>
                {isResolved && (
                  <span className="text-xs text-green-600 font-medium">Resolved</span>
                )}
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                {comment.content}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
